import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Zap, CreditCard, Wifi } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useContent } from "@/lib/hooks";

const formatPrice = (price) =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);

export default function PackageDetail() {
  const { id } = useParams();
  const { t, ml } = useLang();
  const { data: packages = [], isLoading } = useContent("/content/packages");

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p className="text-zinc-500">{t("common.loading")}</p>
      </div>
    );
  }

  const pkg = packages.find((p) => p.id === id);

  if (!pkg) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
        <p className="font-display text-6xl font-extrabold text-zinc-200">404</p>
        <p className="mt-4 text-zinc-500">
          {t("packages.detailTitle")} bulunamadı.
        </p>
        <Link
          to="/tarifeler"
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#0D9488] px-6 py-3 text-sm font-bold text-white transition-all hover:bg-[#0F766E]"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          {t("packages.detailBack")}
        </Link>
      </div>
    );
  }

  const features = ml(pkg.features);
  const featureList = Array.isArray(features) ? features : [];
  const speed = pkg.speed || 0;
  const popular = Boolean(pkg.popular);

  return (
    <div data-testid="package-detail-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#062B2B] pt-32 pb-20">
        <div className="absolute inset-0 bg-radial-teal" />
        <div className="pointer-events-none absolute -start-32 top-0 h-96 w-96 rounded-full bg-[#0D9488]/10 blur-3xl" />
        <div className="pointer-events-none absolute -end-32 bottom-0 h-96 w-96 rounded-full bg-[#2DD4BF]/8 blur-3xl" />

        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <Link
            to="/tarifeler"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#2DD4BF] transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            {t("packages.detailBack")}
          </Link>

          <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              {popular && (
                <span className="inline-flex items-center gap-2 rounded-lg bg-[#2DD4BF]/15 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#2DD4BF]">
                  <Zap className="h-3.5 w-3.5" />
                  {t("packages.popular")}
                </span>
              )}
              <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                {pkg.name}
              </h1>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-zinc-400">
                {t("packages.subtitle")}
              </p>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="relative flex flex-col items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-[#2DD4BF]/10 blur-3xl" />
                <div className="relative flex h-48 w-48 items-center justify-center rounded-full border-4 border-[#2DD4BF]/30 bg-[#062B2B]">
                  <div className="text-center">
                    <span className="font-display text-6xl font-extrabold text-white">{speed}</span>
                    <span className="block text-base font-bold text-[#2DD4BF]">{t("packages.mbps")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="bg-white">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-3">
            {/* Speed Card */}
            <div className="rounded-2xl border border-zinc-200 bg-[#F8FAFB] p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#0D9488] to-[#0891B2] text-white">
                <Wifi className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-display text-lg font-bold text-[#062B2B]">{t("packages.detailSpeed")}</h3>
              <p className="mt-1 text-sm text-zinc-500">{t("packages.detailSpeedDesc")}</p>
              <div className="mt-4 flex items-baseline gap-1.5">
                <span className="font-display text-4xl font-extrabold text-[#062B2B]">{speed}</span>
                <span className="text-base font-bold text-[#0D9488]">{t("packages.mbps")}</span>
              </div>
            </div>

            {/* Price Card */}
            <div className="rounded-2xl border border-zinc-200 bg-[#F8FAFB] p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#0D9488] to-[#0891B2] text-white">
                <CreditCard className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-display text-lg font-bold text-[#062B2B]">{t("packages.detailPrice")}</h3>
              <p className="mt-1 text-sm text-zinc-500">{t("packages.detailPriceDesc")}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-4xl font-extrabold text-[#062B2B]">₺{formatPrice(pkg.price)}</span>
                <span className="text-base text-zinc-500">{t("packages.perMonth")}</span>
              </div>
            </div>

            {/* Features Card */}
            <div className="rounded-2xl border border-zinc-200 bg-[#F8FAFB] p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#0D9488] to-[#0891B2] text-white">
                <Check className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-display text-lg font-bold text-[#062B2B]">{t("packages.detailFeatures")}</h3>
              <p className="mt-1 text-sm text-zinc-500">{t("packages.detailFeaturesDesc")}</p>
              <ul className="mt-4 space-y-2.5">
                {featureList.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[#F0FDFA] text-[#0D9488]">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <span className="text-zinc-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 rounded-2xl bg-gradient-to-r from-[#062B2B] to-[#0F3838] p-10 text-center">
            <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">
              {t("packages.detailCta")}
            </h2>
            <p className="mt-3 text-zinc-400">
              {t("packages.note")}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <Link
                to="/iletisim"
                className="inline-flex items-center gap-2 rounded-xl bg-[#2DD4BF] px-8 py-4 text-sm font-bold text-[#042E2E] transition-all hover:bg-[#14B8A6] hover:shadow-lg hover:shadow-[#2DD4BF]/20"
              >
                {t("nav.applyNow")}
                <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
              <Link
                to="/tarifeler"
                className="inline-flex items-center gap-2 rounded-xl border border-white/25 px-8 py-4 text-sm font-bold text-white transition-all hover:bg-white/10"
              >
                {t("packages.detailBack")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
